import Immutable, { Record as record, List } from 'immutable';
import _ from 'lodash';
import moment from 'moment';

import { sum, compact, unique, isImmutable } from '../helpers/Immutable';
import {
    FareCategories,
    FareClasses,
    NRELinkFareClasses,
    FlexiSeasonValidation,
    TPE_GROUP_SAVE_CODE
} from '../constants/AppConstants';
import Route from './Route';
import Setter from './Setter';
import Ticket from './Ticket';
import Station from './Station';
import FarePriceComparison from './FarePriceComparison';
import TicketType from './TicketType';
import Validity from './Validity';
import { construct, resolve, resolveAll, fields } from '../helpers/LinkMapper';
import { translate as t } from '../lib/I18n';
import { TransPennineStaffRoverNlcCodes } from '../constants/AppConstants';
import SplitInfo from './SplitInfo';

const defaults = {
    id: null,
    category: null,
    alternateDestinations: new List(),
    alternateOrigins: new List(),
    destination: new Station(),
    origin: new Station(),
    price: null,
    totalPrice: null,
    originalTotalPrice: null,
    route: new Route(),
    tickets: new List(),
    ticketType: new TicketType(),
    validFrom: null,
    validUntil: null,
    outwardValidity: null,
    returnValidity: null,
    utsZones: new List(),
    restriction: null,
    seatAvailability: null,
    canSelectSeatOnJourney: null,
    canSelectSeatOnLeg: null,
    discountCode: null,
    discountPercent: null,
    discountName: null,
    discountDescription: null,
    discountFixedAmount: null,
    priceComparison: new FarePriceComparison(),
    numberOfPasses: null,
    setter: new Setter(),
    splitId: null,
    splitSaving: null,
    splitSections: null,
    splitInfo: new SplitInfo(),
};

// borrowed & es6ified from http://stackoverflow.com/a/30033564
function rad2degr (rad) { return rad * 180 / Math.PI; }
function degr2rad (degr) { return degr * Math.PI / 180; }

/**
 * @param latLngInDeg array of arrays with latitude and longtitude
 *   pairs in degrees. e.g. [[latitude1, longtitude1], [latitude2
 *   [longtitude2] ...]
 *
 * @return array with the center latitude longtitude pairs in
 *   degrees.
 */
export function getLatLngCenter (latLngInDegr) {
    let [sumX, sumY, sumZ] = [0, 0, 0];

    latLngInDegr.forEach(([lat, lng]) => {
        lat = degr2rad(lat);
        lng = degr2rad(lng);
        // sum of cartesian coordinates
        sumX += Math.cos(lat) * Math.cos(lng);
        sumY += Math.cos(lat) * Math.sin(lng);
        sumZ += Math.sin(lat);
    });

    const avgX = sumX / latLngInDegr.size;
    const avgY = sumY / latLngInDegr.size;
    const avgZ = sumZ / latLngInDegr.size;

    // convert average x, y, z coordinate to latitude and longtitude
    const lng = Math.atan2(avgY, avgX);
    const hyp = Math.sqrt(avgX * avgX + avgY * avgY);
    const lat = Math.atan2(avgZ, hyp);

    return [rad2degr(lat), rad2degr(lng)];
}

class Fare extends record(defaults) {

    static fromJSON (json = {}) {

        const originWithLocation = _.partial(Fare.getStationWithLocation, _.partial.placeholder, resolveAll(json.alternateOrigins).as(Station));
        const destinationWithLocation = _.partial(Fare.getStationWithLocation, _.partial.placeholder, resolveAll(json.alternateDestinations).as(Station));

        return construct(Fare, json, {
            id: json.id,
            category: fields.string,
            origin: resolve.with(originWithLocation),
            destination: resolve.with(destinationWithLocation),
            price: resolve.with(Immutable.fromJS),
            totalPrice: fields.number,
            originalTotalPrice: fields.number,
            route: resolve.as(Route),
            tickets: resolveAll.as(Ticket),
            ticketType: resolve.as(TicketType),
            outwardValidity: resolve.as(Validity),
            returnValidity: resolve.as(Validity),
            alternateOrigins: resolveAll.as(Station),
            alternateDestinations: resolveAll.as(Station),
            utsZones: resolveAll.with(Number),
            restriction: fields.string,
            discountCode: fields.string,
            discountPercent: fields.number,
            discountFixedAmount: fields.number,
            seatAvailability: fields.number,
            canSelectSeatOnJourney: json.canSelectSeatOnJourney || null,
            canSelectSeatOnLeg: json.canSelectSeatOnLeg || null,
            discountName: fields.string,
            discountDescription: fields.string,
            priceComparison: resolve.as(FarePriceComparison),
            numberOfPasses: fields.number,
            setter: resolve.as(Setter),
            splitId: json.splitId,
            splitSaving: json.splitSaving,
            splitSections: json.splitSections,
            splitInfo: resolve(json.splitInfo).as(SplitInfo),
        });
    }

    getPrice () {
        const price = this.totalPrice || 0;

        return price;
    }

    getAssignedSeatStatus (legs = null) {
        const legMap = this.canSelectSeatOnLeg;

        if (!legMap || Object.keys(legMap).length === 0) {
            return null;
        }

        let relevantLegMap = legMap;
        if (legs && legs.size > 0) {
            const trainLegMap = Object.keys(legMap).reduce((acc, legId) => {
                const matchingLeg = legs.find(leg => leg.legId === legId);
                if (matchingLeg && matchingLeg.isTrainLeg()) {
                    const isCountedPlace = matchingLeg.getIn(['serviceDetails', 'isCountedPlace']);
                    acc[legId] = isCountedPlace ? false : legMap[legId];
                }
                return acc;
            }, {});

            if (Object.keys(trainLegMap).length > 0) {
                relevantLegMap = trainLegMap;
            }
        }

        const values = Object.values(relevantLegMap);

        if (values.every(v => v === false)) { return 'unavailable'; }
        if (values.every(v => v === true)) { return 'available'; }
        return 'partial';
    }

    canSelectSeatForJourney (journeyId) {
        if (!this.canSelectSeatOnJourney || !journeyId) { return null; }
        const val = this.canSelectSeatOnJourney[journeyId];
        if (val === true) { return true; }
        if (val === false) { return false; }
        return null;
    }

    getPricePerFlexiPass () {
        const price = this.totalPrice || 0;
        return price > 0 ? (price / FlexiSeasonValidation.PASSES_PER_FLEXI) : 0;
    }

    getName () {
        const ticketName = this.ticketType.name;
        const subTypes = this.getSubTicketTypes().map((type) => type.name).join(', ');
        if (subTypes) {
            return t('booking.fares.name.withSubtypes', { ticketName, subTypes });
        } else {
            return ticketName;
        }
    }

    getDescription () { return this.route.description && this.route.description.length > 1 ? this.route.description : null; }

    getOutwardDuration () {
        return this.outwardValidity.period.toDuration();
    }

    getTotalPrice () { return sum(this.tickets, 'price'); }

    getMatchingTicketPrice (price) {
        const sortedTickets = this.tickets.sortBy(x => x.price);
        const match = sortedTickets.find(ticket => ticket.price >= price);
        return match && match.price;
    }

    hasMatchingOrLowerTicketPrice (price) {
        return this.tickets.some(ticket => ticket.price ? ticket.price <= price : ticket.totalPrice <= price);
    }

    hasMatchingOrHigherTicketPrice (price) {
        return this.tickets.some(ticket => ticket.price ? ticket.price >= price : ticket.totalPrice >= price);
    }

    hasMatchingTicketType (ticketType) {
        return this.ticketType.code === ticketType;
    }

    isReturn () { return this.ticketType.isReturn; }

    isStandard () { return !this.ticketType.isFirstClass; }

    isFirstClass () { return this.ticketType.isFirstClass; }

    getClass () { return this.isStandard() ? FareClasses.STANDARD : FareClasses.FIRST_CLASS; }

    isCarnet () { return this.ticketType.isCarnet; }

    isGroupSave () { return this.tickets.some((ticket) => ticket.isGroupSave()); }

    isDiscounted () { return !!this.discountPercent || this.tickets.some((ticket) => ticket.hasPromoDiscount()); }

    hasRailcard () { return this.tickets.some((ticket) => ticket.railcard && !ticket.isGroupSave()); }

    getDiscountName () {
        if (!this.isDiscounted() || this.isGroupSave() || this.hasRailcard()) {
            return null;
        }
        return this.discountName || null;
    }

    hasRailcardDiscount () { return this.tickets.some((ticket) => ticket.railcardDiscount > 0 && !ticket.isGroupSave()); }

    hasAppliedRailcard () {
        return this.hasRailcard() && this.hasRailcardDiscount();
    }

    isTravelcard () { return this.origin.isTravelcard() || this.destination.isTravelcard(); }

    isDaySave () { return this.ticketType.isDaySave(); }

    isDaySaveOffPeak () { return this.ticketType.isDaySaveOffPeak(); }

    isRoversRangers () { return this.ticketType.isRoversRangers(); }

    isSeatCompulsory () { return this.ticketType.isAdvance; }

    isPlusBus () { return this.ticketType.isPlusBus(); }

    isSeason () { return this.ticketType.isSeason; }

    isFlexi () { return this.ticketType.isFlexi; }

    isOffPeak () { return this.category === FareCategories.OFF_PEAK; }

    isAnytime () { return this.category === FareCategories.ANYTIME; }

    isAdvance () {
        const ticketName = this.getName();
        return this.category === FareCategories.ADVANCE || ticketName === 'ADVANCE NETWORK WEEKEND';
    }

    isReorderable () {
        return !(this.isPlusBus() || this.isAdvance() || this.isDaySave() || this.isRoversRangers());
    }

    isSmartcard () {
        return this.tickets.some((ticket) => ticket.getIn(['passenger', 'smartcardNumber']));
    }

    getValidFrom () {
        return this.outwardValidity.from;
    }

    getOriginalTotalPrice () {
        if (this.isDiscounted()) {
            return this.originalTotalPrice;
        }
        return sum(this.tickets, 'railcardDiscount') + this.getPrice();
    }

    getPreDiscountPrice () {
        const originalPrice = this.getOriginalTotalPrice();
        return this.isDiscounted() && originalPrice && originalPrice > this.getPrice()
            ? originalPrice
            : null;
    }

    getPromotionalSaving () {
        if (!this.isDiscounted() || !this.originalTotalPrice) {
            return 0;
        }
        const saving =
            this.getOriginalTotalPrice() - this.getPrice() - sum(this.tickets, 'railcardDiscount');

        return Math.max(0, saving);
    }

    getSubTicketTypes () {
        const ticketTypes = compact(this.tickets.map((ticket) => ticket.ticketTypeOverride));
        return unique(ticketTypes, (type) => type.code);
    }

    getPassengers () {
        return compact(this.tickets.map((ticket) => ticket.passenger));
    }

    getPassengerCounts () {
        const children = sum(this.tickets, 'children');
        const adults = sum(this.tickets, 'adults');
        return { adults, children };
    }

    adults () {
        return sum(this.tickets, 'adults');
    }

    children () {
        return sum(this.tickets, 'children');
    }

    getRailcardCounts () {
        return this.tickets.reduce((acc, { railcard }) => {
            if (railcard && !railcard.isGroupSave()) {
                const { code } = railcard;
                acc[code] = acc[code] || 0;
                acc[code]++;
            }
            return acc;
        }, {});
    }

    getAllRailcards () {
        return this.tickets.reduce((railcards, { railcard }) => {
            if (railcard && railcard.code && !railcard.isGroupSave()) {
                railcards = railcards.push(railcard);
            }
            return railcards;
        }, new List());
    }

    getCarnetInformation () {
        return this.ticketType.carnetInformation;
    }

    getFlexiInformation () {
        return this.ticketType.flexiInformation;
    }

    static getStationWithLocation (station, alternativeStations) {
        station = isImmutable(station) ? station : resolve(station).as(Station);
        if (station.latitude && station.longitude) {
            return station;
        }

        const [ latitude, longitude ] = getLatLngCenter(alternativeStations.map((alt => [alt.latitude, alt.longitude])));
        return station.merge({ latitude, longitude });
    }

    getOutwardValidityPeriod () {
        return this.outwardValidity.period;
    }

    getOutwardValidityDurationString () {
        return this.getOutwardValidityPeriod().toDuration().toJSON();
    }

    hasPriceComparison () {
        return this.priceComparison && !this.priceComparison.isEmpty();
    }

    getNRELinkParams () {
        let fareClass = NRELinkFareClasses.STANDARD;
        if (this.isFirstClass()) {
            fareClass = NRELinkFareClasses.FIRST;
        }
        return {
            origin: this.origin.crs || this.origin.nlc,
            destination: this.destination.crs || this.destination.nlc,
            outwardDate: moment(this.outwardValidity.from).format('DD-MMM-YY'),
            fareClass
        };
    }

    getNRELinkQuery () {
        let query = {};
        if (this.isReturn()) {
            _.set(query, 'returnDate', moment(this.returnValidity.from).format('DD-MMM-YY'));
        }
        return query;
    }

    getNumberOfPasses () {
        if (this.isFlexi()) {
            return this.ticketType.flexiInformation.numberOfTickets;
        }

        return null;
    }

    setNumberOfPasses (numberOfPasses = FlexiSeasonValidation.PASSES_PER_FLEXI) {
        return this.set('numberOfPasses', numberOfPasses);
    }

    includesJCPRailcardDiscount () {
        return this.tickets.some((ticket) => ticket.railcard && ticket.railcard.code === 'JCP');
    }

    isWeeklyCapAvailable () {
        const isWeeklyTicket = this.getOutwardDuration().toJSON() === 'P7D';
        return isWeeklyTicket && this.origin.weeklyCapAvailable && this.destination.weeklyCapAvailable;
    }

    isTPEGroupSave () {
        return this.discountCode === TPE_GROUP_SAVE_CODE;
    }

    isTPEStaffRover () {
        const originNlc = _.get(this, 'origin.nlc', null);
        const destinationNlc = _.get(this, 'destination.nlc', null);

        if (!originNlc || !destinationNlc) {
            return false;
        }

        return originNlc === TransPennineStaffRoverNlcCodes.ORIGIN && destinationNlc === TransPennineStaffRoverNlcCodes.DESTINATION;
    }

    isPartOfSplit() {
        return !!this.splitId;
    }

    isSplitReturn() {
        const hasSplitSections = this.splitInfo?.splitSections?.length === 2;
        return this.isReturn() && hasSplitSections;
    }

    isValidForSingleReturn(anotherFare) {
        if (!anotherFare) {
            return false;
        }

        const originNlc = this.origin && this.origin.nlc;
        const destinationNlc = this.destination && this.destination.nlc;
        const anotherOriginNlc = anotherFare.origin && anotherFare.origin.nlc;
        const anotherDestinationNlc = anotherFare.destination && anotherFare.destination.nlc;

        return originNlc === anotherDestinationNlc && destinationNlc === anotherOriginNlc;
    }
}

export default Fare;
